import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAccessibility } from '@/hooks/useAccessibility';
import { 
  Accessibility, X, RotateCcw, 
  Type, Eye, MousePointer, Link2, 
  ZapOff, Palette, Focus, ImageOff, 
  BookOpen, AlignJustify, Space, TextCursorInput
} from 'lucide-react';

const AccessibilityWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { settings, updateSetting, resetAll } = useAccessibility();
  const panelRef = useRef<HTMLDivElement>(null);
  const toggleBtnRef = useRef<HTMLButtonElement>(null);

  // Focus trap and keyboard handling for the dialog
  useEffect(() => {
    if (!isOpen || !panelRef.current) return;

    // Focus first focusable element in the panel
    const firstBtn = panelRef.current.querySelector<HTMLButtonElement>('button');
    firstBtn?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        toggleBtnRef.current?.focus();
        return;
      }

      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'button, a, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const cycleValue = (key: 'fontSize' | 'saturation' | 'lineHeight', max: number) => {
    const current = settings[key];
    updateSetting(key, ((current + 1) % (max + 1)) as any);
  };

  const items = [
    { 
      label: 'הגדלת טקסט', 
      icon: Type, 
      active: settings.fontSize > 0,
      badge: settings.fontSize > 0 ? `+${settings.fontSize * 20}%` : undefined,
      onClick: () => cycleValue('fontSize', 2)
    },
    { 
      label: 'ניגודיות גבוהה', 
      icon: Eye, 
      active: settings.highContrast,
      onClick: () => updateSetting('highContrast', !settings.highContrast)
    },
    { 
      label: 'צמצום אנימציות', 
      icon: ZapOff, 
      active: settings.reducedMotion,
      onClick: () => updateSetting('reducedMotion', !settings.reducedMotion)
    },
    { 
      label: 'פונט דיסלקטי', 
      icon: TextCursorInput, 
      active: settings.dyslexicFont,
      onClick: () => updateSetting('dyslexicFont', !settings.dyslexicFont)
    },
    { 
      label: 'הדגשת קישורים', 
      icon: Link2, 
      active: settings.linkHighlight,
      onClick: () => updateSetting('linkHighlight', !settings.linkHighlight)
    },
    { 
      label: 'סמן גדול', 
      icon: MousePointer, 
      active: settings.bigCursor,
      onClick: () => updateSetting('bigCursor', !settings.bigCursor)
    },
    { 
      label: 'ריווח טקסט', 
      icon: Space, 
      active: settings.textSpacing,
      onClick: () => updateSetting('textSpacing', !settings.textSpacing)
    },
    { 
      label: 'רוויה', 
      icon: Palette, 
      active: settings.saturation > 0,
      badge: settings.saturation === 1 ? 'נמוכה' : settings.saturation === 2 ? 'אפור' : undefined,
      onClick: () => cycleValue('saturation', 2)
    },
    { 
      label: 'הדגשת מיקוד', 
      icon: Focus, 
      active: settings.focusHighlight,
      onClick: () => updateSetting('focusHighlight', !settings.focusHighlight)
    },
    { 
      label: 'הסתרת תמונות', 
      icon: ImageOff, 
      active: settings.hideImages,
      onClick: () => updateSetting('hideImages', !settings.hideImages)
    },
    { 
      label: 'פונט קריא', 
      icon: BookOpen, 
      active: settings.readableFont,
      onClick: () => updateSetting('readableFont', !settings.readableFont)
    },
    { 
      label: 'גובה שורה', 
      icon: AlignJustify, 
      active: settings.lineHeight > 0,
      badge: settings.lineHeight === 1 ? 'גדול' : settings.lineHeight === 2 ? 'גדול מאוד' : undefined,
      onClick: () => cycleValue('lineHeight', 2)
    },
  ];

  return (
    <>
      {/* Floating Toggle Button */}
      <button
        ref={toggleBtnRef}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed left-[16px] md:left-auto md:right-[20px] bottom-[calc(80px+env(safe-area-inset-bottom,0px))] z-[9998] w-11 h-11 rounded-full bg-[var(--cin-ink)] text-white shadow-[0_6px_18px_-6px_rgba(15,23,41,0.55)] transition-transform duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] flex items-center justify-center hover:scale-105 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--cin-blue)]/40"
        aria-label={isOpen ? 'סגור תפריט נגישות' : 'פתח תפריט נגישות'}
        aria-expanded={isOpen}
        aria-controls="accessibility-panel"
      >
        {isOpen ? <X className="w-5 h-5" /> : <Accessibility className="w-5 h-5" />}
      </button>

      {/* Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-[10001]" 
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          
          {/* Widget Panel */}
          <div
            ref={panelRef}
            id="accessibility-panel"
            role="dialog"
            aria-label="הגדרות נגישות"
            aria-modal="true"
            className="fixed bottom-[calc(136px+env(safe-area-inset-bottom,0px))] left-4 md:left-auto md:right-4 z-[10002] w-80 max-w-[calc(100vw-2rem)] max-h-[calc(100svh-180px)] overflow-y-auto rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl"
            dir="rtl"
          >
            {/* Header */}
            <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <Accessibility className="w-5 h-5 text-cyan-400" />
                <h2 className="text-lg font-bold text-white">הגדרות נגישות</h2>
              </div>
              <button
                onClick={resetAll}
                className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors px-2 py-1 rounded-lg hover:bg-slate-800"
                aria-label="איפוס כל ההגדרות"
              >
                <RotateCcw className="w-4 h-4" />
                איפוס
              </button>
            </div>

            {/* Settings Grid */}
            <div className="p-4 grid grid-cols-3 gap-2">
              {items.map((item) => {
                const IconComp = item.icon;
                return (
                  <button
                    key={item.label}
                    onClick={item.onClick}
                    className={`relative flex flex-col items-center gap-1.5 p-3 rounded-xl text-center transition duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                      item.active 
                        ? 'bg-cyan-500/20 border border-cyan-500/50 text-cyan-400' 
                        : 'bg-slate-800 border border-slate-700 text-slate-400 hover:bg-slate-750 hover:text-slate-300'
                    }`}
                    aria-pressed={item.active}
                    aria-label={`${item.label}${item.badge ? ` - ${item.badge}` : ''}${item.active ? ' - פעיל' : ''}`}
                  >
                    <IconComp className="w-5 h-5" />
                    <span className="text-[10px] leading-tight font-medium">{item.label}</span>
                    {item.badge && (
                      <span className="absolute -top-1 -left-1 text-[8px] bg-cyan-500 text-slate-900 px-1.5 py-0.5 rounded-full font-bold">
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="border-t border-slate-700 p-3 text-center">
              <a 
                href="/accessibility" 
                className="text-xs text-cyan-400 hover:text-cyan-300 underline transition-colors"
              >
                הצהרת נגישות מלאה
              </a>
              <p className="text-[10px] text-slate-500 mt-1">תקן WCAG 2.2 AA | תקן ישראלי 5568</p>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AccessibilityWidget;
